import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Quotes from './Quotes';
import { TruckIcon, PackageIcon, AlertTriangleIcon, ClockIcon, CheckCircleIcon, ArrowRightIcon, CalendarIcon, MapPinIcon, PlusIcon, SearchIcon, FileBarChart2Icon, LifeBuoyIcon, TrendingUpIcon, TrendingDownIcon, FilterIcon, XIcon } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
interface KPI {
  title: string;
  value: string;
  trend: number;
  previousValue: string;
  icon: React.ReactNode;
  color: string;
}
interface Location {
  id: string;
  name: string;
  city: string;
  state: string;
  lastUsed: string;
  type: string;
}
const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  // Mock data loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  const kpis: KPI[] = [{
    title: 'Active Shipments',
    value: '24',
    trend: 5,
    previousValue: '19',
    icon: <TruckIcon size={24} data-id="element-546" />,
    color: 'text-blue-600'
  }, {
    title: 'Pending Shipments',
    value: '12',
    trend: -2,
    previousValue: '14',
    icon: <ClockIcon size={24} data-id="element-547" />,
    color: 'text-amber-500'
  }, {
    title: 'On-Time Performance',
    value: '94%',
    trend: 2,
    previousValue: '92%',
    icon: <CheckCircleIcon size={24} data-id="element-548" />,
    color: 'text-green-600'
  }, {
    title: 'At-Risk Shipments',
    value: '3',
    trend: 1,
    previousValue: '2',
    icon: <AlertTriangleIcon size={24} data-id="element-549" />,
    color: 'text-red-600'
  }, {
    title: 'Monthly Volume',
    value: '156',
    trend: 12,
    previousValue: '144',
    icon: <PackageIcon size={24} data-id="element-550" />,
    color: 'text-purple-600'
  }, {
    title: 'Avg Transit Time',
    value: '2.3 days',
    trend: -0.2,
    previousValue: '2.5 days',
    icon: <CalendarIcon size={24} data-id="element-551" />,
    color: 'text-indigo-600'
  }];
  const recentLoads = [{
    id: 'SH-12345',
    origin: 'Chicago, IL',
    destination: 'Dallas, TX',
    status: 'In Transit',
    pickupDate: '2024-01-15',
    deliveryDate: '2024-01-17',
    statusColor: 'bg-blue-500'
  }, {
    id: 'SH-12344',
    origin: 'New York, NY',
    destination: 'Miami, FL',
    status: 'Delivered',
    pickupDate: '2024-01-14',
    deliveryDate: '2024-01-16',
    statusColor: 'bg-green-500'
  }, {
    id: 'SH-12343',
    origin: 'Seattle, WA',
    destination: 'Los Angeles, CA',
    status: 'Pending Pickup',
    pickupDate: '2024-01-16',
    deliveryDate: '2024-01-18',
    statusColor: 'bg-amber-500'
  }, {
    id: 'SH-12342',
    origin: 'Boston, MA',
    destination: 'Washington, DC',
    status: 'At Risk',
    pickupDate: '2024-01-15',
    deliveryDate: '2024-01-16',
    statusColor: 'bg-red-500'
  }, {
    id: 'SH-12341',
    origin: 'Denver, CO',
    destination: 'Phoenix, AZ',
    status: 'Delivered',
    pickupDate: '2024-01-13',
    deliveryDate: '2024-01-15',
    statusColor: 'bg-green-500'
  }];
  const frequentLocations: Location[] = [{
    id: '1',
    name: 'Main Distribution Center',
    city: 'Chicago',
    state: 'IL',
    lastUsed: '2024-01-15',
    type: 'Warehouse'
  }, {
    id: '2',
    name: 'Northeast Hub',
    city: 'Boston',
    state: 'MA',
    lastUsed: '2024-01-14',
    type: 'Distribution Center'
  }, {
    id: '3',
    name: 'West Coast Facility',
    city: 'Los Angeles',
    state: 'CA',
    lastUsed: '2024-01-13',
    type: 'Warehouse'
  }];
  const volumeData = [{
    date: 'Jan 10',
    shipments: 42
  }, {
    date: 'Jan 11',
    shipments: 38
  }, {
    date: 'Jan 12',
    shipments: 45
  }, {
    date: 'Jan 13',
    shipments: 39
  }, {
    date: 'Jan 14',
    shipments: 41
  }, {
    date: 'Jan 15',
    shipments: 48
  }, {
    date: 'Jan 16',
    shipments: 46
  }];
  const renderSkeleton = () => <div className="space-y-6 animate-pulse" data-id="element-552">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-553">
        {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="bg-white rounded-lg shadow-sm p-4" data-id="element-554">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-2" data-id="element-555"></div>
            <div className="h-8 bg-gray-200 rounded w-1/2" data-id="element-556"></div>
          </div>)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-557">
        <div className="bg-white rounded-lg shadow-sm p-4" data-id="element-558">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" data-id="element-559"></div>
          <div className="h-48 bg-gray-200 rounded" data-id="element-560"></div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4" data-id="element-561">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" data-id="element-562"></div>
          <div className="space-y-3" data-id="element-563">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-12 bg-gray-200 rounded" data-id="element-564"></div>)}
          </div>
        </div>
      </div>
    </div>;
  return <div className="py-4" data-id="element-565">
      {/* Show quote dialog when needed */}
      {showQuoteDialog && <Quotes data-id="element-566" />}
      {/* Header section */}
      <div className="mb-6" data-id="element-567">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-568">Dashboard</h1>
        <p className="text-gray-600" data-id="element-569">
          Welcome back! Here's your shipment overview.
        </p>
      </div>
      {/* Quick Actions Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6" data-id="element-570">
        <div className="p-4 flex flex-wrap gap-3" data-id="element-571">
          <button onClick={() => setShowQuoteDialog(true)} className="flex items-center px-4 py-2.5 bg-primary-700 text-white rounded-md hover:bg-primary-800 transition-colors" data-id="element-572">
            <PlusIcon size={18} className="mr-2" data-id="element-573" />
            <span className="font-medium" data-id="element-574">Get a Quote</span>
          </button>
          <div className="flex flex-wrap gap-3" data-id="element-575">
            <Link to="/locations" className="flex items-center px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors" data-id="element-576">
              <MapPinIcon size={18} className="text-primary-700 mr-2" data-id="element-577" />
              <span className="font-medium text-gray-700" data-id="element-578">Add Location</span>
            </Link>
            <Link to="/shipments" className="flex items-center px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors" data-id="element-579">
              <TruckIcon size={18} className="text-primary-700 mr-2" data-id="element-580" />
              <span className="font-medium text-gray-700" data-id="element-581">
                View All Shipments
              </span>
            </Link>
            <Link to="/support" className="flex items-center px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors" data-id="element-582">
              <LifeBuoyIcon size={18} className="text-primary-700 mr-2" data-id="element-583" />
              <span className="font-medium text-gray-700" data-id="element-584">Get Help</span>
            </Link>
          </div>
        </div>
      </div>
      {loading ? renderSkeleton() : <div className="space-y-6" data-id="element-585">
          {/* KPIs Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-586">
            {kpis.map((kpi, index) => <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow" data-id="element-587">
                <div className="flex items-center justify-between mb-3" data-id="element-588">
                  <div className={`p-2.5 rounded-lg bg-opacity-10 ${kpi.color.replace('text-', 'bg-')}`} data-id="element-589">
                    {kpi.icon}
                  </div>
                  <span className={`flex items-center text-sm font-medium ${kpi.trend > 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-590">
                    {kpi.trend > 0 ? <TrendingUpIcon size={16} className="mr-1" data-id="element-591" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-592" />}
                    {Math.abs(kpi.trend)}%
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-593">
                  {kpi.value}
                </h3>
                <p className="text-sm font-medium text-gray-600" data-id="element-594">{kpi.title}</p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-595">
                  vs previous ({kpi.previousValue})
                </p>
              </div>)}
          </div>
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-596">
            {/* Volume Trend Chart */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200" data-id="element-597">
              <div className="p-5" data-id="element-598">
                <div className="flex items-center justify-between mb-6" data-id="element-599">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-600">
                    Shipment Volume Trend
                  </h3>
                  <select className="text-sm border border-gray-200 rounded-md px-3 py-1.5 bg-gray-50" data-id="element-601">
                    <option data-id="element-602">Last 7 days</option>
                    <option data-id="element-603">Last 30 days</option>
                    <option data-id="element-604">Last 90 days</option>
                  </select>
                </div>
                <div className="h-[400px]" data-id="element-605">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-606">
                    <AreaChart data={volumeData} data-id="element-607">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" data-id="element-608" />
                      <XAxis dataKey="date" stroke="#737373" fontSize={12} tickLine={false} axisLine={{
                    stroke: '#E5E5E5'
                  }} padding={{
                    left: 10,
                    right: 10
                  }} data-id="element-609" />
                      <YAxis stroke="#737373" fontSize={12} tickLine={false} axisLine={false} tickFormatter={value => `${value}`} data-id="element-610" />
                      <Tooltip contentStyle={{
                    background: '#FFF',
                    border: '1px solid #E5E5E5',
                    borderRadius: '6px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    padding: '8px 12px'
                  }} labelStyle={{
                    color: '#404040',
                    fontWeight: 500
                  }} itemStyle={{
                    color: '#d52724'
                  }} data-id="element-611" />
                      <Area type="monotone" dataKey="shipments" stroke="#d52724" fill="#fee2e2" strokeWidth={2} data-id="element-612" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            {/* Recent Loads */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200" data-id="element-613">
              <div className="p-5" data-id="element-614">
                <div className="flex justify-between items-center mb-4" data-id="element-615">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-616">
                    Recent Loads
                  </h3>
                  <Link to="/shipments" className="text-primary-700 hover:text-primary-800 font-medium flex items-center" data-id="element-617">
                    View all
                    <ArrowRightIcon size={16} className="ml-1" data-id="element-618" />
                  </Link>
                </div>
                <div className="space-y-3" data-id="element-619">
                  {recentLoads.slice(0, 5).map(load => <Link key={load.id} to={`/shipments/${load.id}`} className="block p-3 hover:bg-gray-50 rounded-lg border border-gray-100 transition-all hover:border-gray-200" data-id="element-620">
                      <div className="flex justify-between items-center mb-1" data-id="element-621">
                        <span className="font-medium text-primary-700" data-id="element-622">
                          {load.id}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${load.status === 'Delivered' ? 'bg-green-100 text-green-800' : load.status === 'In Transit' ? 'bg-blue-100 text-blue-800' : load.status === 'Pending Pickup' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`} data-id="element-623">
                          {load.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 flex items-center" data-id="element-624">
                        <MapPinIcon size={14} className="mr-1.5 text-gray-400" data-id="element-625" />
                        {load.origin} → {load.destination}
                      </div>
                    </Link>)}
                </div>
              </div>
            </div>
            {/* Frequent Locations */}
            <div className="lg:col-span-3 bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-626">
              <div className="flex justify-between items-center mb-3" data-id="element-627">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-628">
                  Frequent Locations
                </h3>
                <Link to="/locations" className="text-primary-700 hover:text-primary-800 text-sm font-medium flex items-center" data-id="element-629">
                  View all
                  <ArrowRightIcon size={14} className="ml-1" data-id="element-630" />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-631">
                {frequentLocations.map(location => <Link key={location.id} to={`/locations/${location.id}`} className="block p-2 hover:bg-gray-50 rounded-md transition-colors border border-gray-100" data-id="element-632">
                    <div className="flex justify-between" data-id="element-633">
                      <span className="font-medium text-gray-900" data-id="element-634">
                        {location.name}
                      </span>
                      <span className="text-sm text-gray-500" data-id="element-635">
                        {location.type}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1" data-id="element-636">
                      {location.city}, {location.state}
                    </div>
                  </Link>)}
              </div>
            </div>
          </div>
        </div>}
    </div>;
};
export default Dashboard;